import React, { useState, useEffect, useCallback } from "react";
import DataTable from "react-data-table-component";
import "../../assets/css/style.css";
import { customStyles } from "../styles/table-style";
import BASE_URL from "../../assets/global/url";
import axios from "axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ThreeDot } from "react-loading-indicators";
import NoAccess from "../../assets/img/NoAccess.png";
import swal from "sweetalert";
import useDecodeToken from "../../hooks/customHook/useDecodeToken";
import { format } from "date-fns";
import { PaginationControls } from "../../hooks/customHook/paginationHook/usePagination";
import { useServerPagination } from "../../hooks/customHook/paginationHook/useServerPagination";
import DatePicker from "react-datepicker";
import { compactNumberFormat } from "../../utils/numberFormatter";
import DateRangePicker from "../../components/DateRangePicker";
import { getMonthBoundaries, initializeCutoff } from "../../utils/newdate";
import { Button, Modal } from "react-bootstrap";

const OldFixedAsset = ({ authrztn }) => {
  const navigate = useNavigate();
  const userLoggedID = useDecodeToken();
  const [isLoading, setIsLoading] = useState(true); // For fixed asset module loading state
  const [cutOffDate, setCutOffDate] = useState(""); // For date range

  // For forecast modal
  const [showForecast, setShowForecast] = useState(false);
  const [forecast, setForecast] = useState({
    list: [],
    totalAmount: 0,
  });

  // For search filter
  const [searchText, setSearchText] = useState("");
  const [filterColumn, setFilterColumn] = useState("all");

  // For data table pagination
  const pagination = useServerPagination(
    BASE_URL + "/fixedasset/getFixedAsset",
    10
  );

  // Initialize date range and fetch table
  const fetchCutOff = () => {
    const boundaries = getMonthBoundaries();
    const initialCutoff = initializeCutoff(boundaries);

    setCutOffDate({
      from: initialCutoff.from,
      to: initialCutoff.to,
    });
    reloadTable(initialCutoff);
  };

  // Get all fixed asset forecast within selected date range
  const fetchForecast = async (cutoff) => {
    try {
      const res = await axios.get(`${BASE_URL}/fixedasset/forecast`, {
        params: {
          startDate: cutoff?.from,
          endDate: cutoff?.to,
        },
      });

      setForecast(res.data);
    } catch (error) {
      console.error(error);
    }
  };

  // Handle data table fetching
  const reloadTable = (cutoff, searchText = "") => {
    pagination.updateParams({
      startDate: cutoff?.from,
      endDate: cutoff?.to,
      filterColumn,
      searchText,
    });
    fetchForecast(cutoff);
    setIsLoading(false);
  };

  // Update search text and refresh table results
  const handleSearchChange = (value) => {
    reloadTable(cutOffDate, value);
    setSearchText(value);
  };

  // Handle filter column change
  const handleFilterColumnChange = (value) => {
    setSearchText("");
    reloadTable(cutOffDate);
    setFilterColumn(value);
  };

  // Open forecast modal
  const handleOpenForecast = () => {
    setShowForecast(true);
  };

  // Closes the forecast modal
  const handleCloseForecast = () => {
    setShowForecast(false);
  };

  // Handles the payment process for a fixed asset forecast
  const handlePayForecast = async (fixedAssetForecastId) => {
    try {
      const payForecast = await swal({
        icon: "warning",
        title: "Confirm Forecast Payment",
        text: "Are you sure you want to proceed with this fixed asset forecast payment?",
        buttons: true,
        dangerMode: true,
      });

      if (payForecast) {
        const res = await axios.patch(
          `${BASE_URL}/fixedasset/forecast/${fixedAssetForecastId}/payments`
        );

        if (res.status === 200) {
          await swal({
            icon: "success",
            title: "Payment Successful",
            text: "The fixed asset forecast has been marked as paid.",
          });

          fetchForecast(cutOffDate);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  // Marks a fixed asset forecast as passed
  const handlePassForecast = async (fixedAssetId, fixedAssetForecastId) => {
    try {
      const passForecast = await swal({
        icon: "warning",
        title: "Mark Forecast as Passed?",
        text: "This will mark the selected fixed asset forecast as passed and generate a new forecast month. Do you want to continue?",
        buttons: true,
        dangerMode: true,
      });

      if (passForecast) {
        const res = await axios.patch(
          `${BASE_URL}/fixedasset/forecast/${fixedAssetForecastId}`,
          {
            fixedAssetId,
          }
        );

        if (res.status === 200) {
          await swal({
            icon: "success",
            title: "Forecast Passed",
            text: "The fixed asset forecast has been marked as passed.",
          });

          fetchForecast(cutOffDate);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  // For widgets
  const totalCost = pagination.data
    .filter((item) => item.status === "Approved")
    .reduce(
      (total, asset) => total + asset.total_cost * asset.currency_rate,
      0
    );

  const depreciated_amount = pagination.data.reduce(
    (total, asset) => total + asset.depreciated_amount * asset.currency_rate,
    0
  );

  const netValue = pagination.data
    .filter((item) => item.status === "Approved")
    .reduce((total, asset) => total + asset.net_value * asset.currency_rate, 0);

  // For data table column
  const columns = [
    {
      name: "Transaction ID",
      selector: (row) => row.transaction_code,
    },
    {
      name: "Date Depreciated",
      selector: (row) => format(row.date_depreciated, "MMM/dd/yyyy"),
    },
    {
      name: "Item Name",
      selector: (row) => row.product_name,
    },
    {
      name: "Total Cost",
      selector: (row) =>
        (row.total_cost || 0)?.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
    },
    {
      name: "Net Value",
      selector: (row) =>
        row.status === "Pending"
          ? "--"
          : (row.net_value || 0).toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }),
    },
    {
      name: "Months Left",
      selector: (row) =>
        row.status === "Pending"
          ? "--"
          : row.months_left.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }),
    },
    {
      name: "Remarks",
      selector: (row) => row.remarks || "--",
    },
    {
      name: "Status",
      selector: (row) => row.status,
      cell: (row) => {
        let color;
        switch (row.status) {
          case "Pending":
            color = "#FFA500";
            break;
          case "Approved":
            color = "#3B9F3F";
            break;
          case "Rejected":
            color = "#FF0000";
            break;
          default:
            color = "initial";
        }
        return (
          <div
            style={{
              padding: "5px 10px",
              borderRadius: "5px",
              color: color,
              textTransform: "uppercase",
              fontWeight: "bold",
            }}
          >
            {row.status}
          </div>
        );
      },
    },
  ];
  if (authrztn.includes("FixedAssets-Delete")) {
    columns.push({
      name: "Action",
      selector: (row) => (
        <i
          className="fas fa-trash"
          style={{ cursor: "pointer", color: "red", fontSize: "1.5rem" }}
          onClick={() => handleDeleteFixedAsset(row.id, row.date_depreciated)}
        ></i>
      ),
    });
  }

  // Handle fixed asset deletion
  const handleDeleteFixedAsset = async (fixedAssetId, fixedAssetDate) => {
    swal({
      title: "Confirm Deletion",
      text: "Are you sure you want to delete?",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          const response = await axios.delete(
            `${BASE_URL}/fixedasset/deleteFixedAsset/${fixedAssetId}/${fixedAssetDate}`
          );
          if (response.status === 200) {
            swal({
              title: "Fixed Asset Deleted Successfully!",
              text: "The fixed asset has been successfully deleted.",
              icon: "success",
              button: "OK",
            }).then(() => {
              reloadTable(cutOffDate);
            });
          } else if (response.status === 202) {
            const { fixedDate, CutoffName } = response.data;
            swal({
              title: "Delete Prohibited!",
              text: `Fixed asset cannot be deleted as its date (${fixedDate}) falls within the posted cutoff period named "${CutoffName}".`,
              icon: "warning",
              button: "OK",
            });
          } else {
            swal({
              icon: "error",
              title: "Something went wrong",
              text: "Please contact our support team for assistance.",
            });
          }
        } catch (err) {
          if (err.response) {
            swal({
              icon: "error",
              title: "Error",
              text: "An error occurred while deleting the fixed asset.",
            });
          }
        }
      }
    });
  };

  // Handle table row click
  const handleRowClicked = (row) => {
    navigate(`/accounting/view-fixed-asset/${row.id}`);
  };

  // Handle date range change
  const handleDateRangeChange = (startDate, endDate) => {
    const cutoff = {
      id: "custom",
      from: startDate,
      to: endDate,
    };
    setCutOffDate(cutoff);
    setSearchText("");
    pagination.setCurrentPage(1);
    reloadTable(cutoff);
  };

  // Searchable column options for table filtering
  const searchFilterColumn = [
    {
      label: "All",
      value: "all",
    },
    {
      label: "Transaction ID",
      value: "transaction_code",
    },
    {
      label: "Date Depreciated",
      value: "date_depreciated",
    },
    {
      label: "Item Name",
      value: "item_name",
    },
    {
      label: "Total Cost",
      value: "total_cost",
    },
    {
      label: "Remarks",
      value: "remarks",
    },
    {
      label: "Status",
      value: "status",
    },
  ];

  useEffect(() => {
    fetchCutOff();
  }, []);

  return (
    <div className="h-100 w-100 border bg-white custom-container">
      {isLoading ? (
        <div className="loading-container">
          <ThreeDot
            variant="brick-stack"
            color="#6290FE"
            size="large"
            text="Loading Data..."
            textColor=""
          />
        </div>
      ) : authrztn.includes("FixedAssets-View") ? (
        <>
          <div className="w-100 p-2 d-flex flex-row justify-content-between">
            <div className="d-flex flex-column title-custom">
              <span className="fs-3">FIXED ASSETS</span>
            </div>
            <div className="d-flex align-items-center">
              <span
                onClick={() => handleOpenForecast()}
                className="mx-3 text-primary"
                title="Forecast"
                style={{
                  textDecoration: "underline",
                  display: "inline-block",
                  transition: "transform 0.2s ease-in-out",
                  cursor: "pointer",
                  opacity: 1, // Key change: use opacity
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.transform = "scale(1.1)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.transform = "scale(1)")
                }
              >
                Forecast
              </span>
              <div>
                {authrztn.includes("FixedAssets-Add") && (
                  <Link
                    to="/accounting/create-fixed-assets"
                    className="btn btn-primary d-flex flex-row align-items-center title-button"
                  >
                    <i className="bx bx-plus fs-5"></i> Create
                  </Link>
                )}
              </div>
            </div>
          </div>

          {/* Widgets */}
          <div className="row mt-3">
            <div className="col-md-4">
              <div className="card text-center">
                <div className="card-body">
                  <h5 className="card-title">
                    <span>
                      <i class="bx bx-bar-chart-alt fs-5 h-100"></i>
                    </span>
                    Total Cost
                  </h5>
                  <p
                    className="card-text text-success amount"
                    title={totalCost.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                    })}
                    style={{ cursor: "default" }}
                  >
                    {totalCost.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                      style: "currency",
                      currency: "PHP",
                    })}
                    {/* ₱{totalCost ? compactNumberFormat(totalCost) : "0.00"} */}
                  </p>
                  {/* <p className="card-text">INCREASE 12% VS LAST MONTH</p> */}
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card text-center">
                <div className="card-body">
                  <h5 className="card-title">
                    <span>
                      <i class="bx bx-trending-down me-1 h-100"></i>
                    </span>
                    Depreciated Amount
                  </h5>
                  <p
                    className="card-text text-success amount"
                    title={depreciated_amount.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                    })}
                    style={{ cursor: "default" }}
                  >
                    {(depreciated_amount || 0).toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                      style: "currency",
                      currency: "PHP",
                    })}
                    {/* ₱
                    {depreciated_amount
                      ? compactNumberFormat(depreciated_amount)
                      : "0.00"} */}
                  </p>
                  {/* <p className="card-text">INCREASE 12%</p> */}
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card text-center">
                <div className="card-body">
                  <h5 className="card-title d-flex justify-content-center align-items-center">
                    <span>
                      <i class="bx bx-credit-card fs-5 me-1 pt-1"></i>
                    </span>
                    <span>Net Value</span>
                  </h5>
                  <p
                    className="card-text text-success amount"
                    title={netValue.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                    })}
                    style={{ cursor: "default" }}
                  >
                    {(netValue || 0).toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                      style: "currency",
                      currency: "PHP",
                    })}
                    {/* ₱{netValue ? compactNumberFormat(netValue) : "0.00"} */}
                  </p>
                  {/* <p className="card-text">INCREASE 12%</p> */}
                </div>
              </div>
            </div>
          </div>

          {/* Date filter */}
          <div className="container-fluid mt-4 p-0">
            <div className="row mx-auto">
              <div className="col-sm mb-2">
                <DateRangePicker
                  startDate={
                    cutOffDate?.from ? new Date(cutOffDate.from) : null
                  }
                  endDate={cutOffDate?.to ? new Date(cutOffDate.to) : null}
                  onDateRangeChange={handleDateRangeChange}
                  label="Date Range"
                />
              </div>
              <div className="col-sm d-flex flex-row align-items-end mb-2 filter-btn-container w-100"></div>
            </div>
          </div>

          {/* Search filter */}
          <div className="w-100 mt-4 container-fluid">
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                placeholder="Search"
                value={searchText}
                onChange={(e) => handleSearchChange(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-outline-secondary dropdown-toggle-split"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i className="fa-solid fa-sliders"></i>
              </button>
              <ul className="dropdown-menu dropdown-menu-end">
                {searchFilterColumn.map((item) => (
                  <li>
                    <button
                      className={`dropdown-item ${
                        filterColumn === item.value ? "active" : ""
                      }`}
                      onClick={() => handleFilterColumnChange(item.value)}
                    >
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div
            className="w-100 mt-3 container-fluid"
            style={{ maxWidth: "79.5vw" }}
          >
            <div className="data-table-cell-width">
              <DataTable
                columns={columns}
                data={pagination.data}
                customStyles={customStyles}
                onRowClicked={handleRowClicked}
                className="dataTable"
              />
              <PaginationControls {...pagination} />
            </div>
          </div>
        </>
      ) : (
        <div className="no-access">
          <img src={NoAccess} alt="NoAccess" className="no-access-img" />
          <h3>You don't have access to this function.</h3>
        </div>
      )}

      {/* Forecast Modal */}
      <Modal
        show={showForecast}
        onHide={handleCloseForecast}
        size="lg"
        dialogClassName="custom-modal-vw90"
      >
        <Modal.Header>
          <Modal.Title className="w-100">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <span className="fw-semibold fs-5">Fixed Asset Forecast</span>
              </div>

              {forecast?.list?.length > 0 && (
                <div className="text-end">
                  <span className="fw-semibold text-primary fs-5">
                    Total Amount:{" "}
                    {forecast.totalAmount.toLocaleString("en-US", {
                      style: "currency",
                      currency: "PHP",
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
              )}
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {forecast?.list?.length ? (
            <>
              {forecast?.list?.map((item) => (
                <React.Fragment key={item.id}>
                  <div className="card p-3 mb-4 shadow-sm border-0 rounded">
                    <div className="d-flex justify-content-between">
                      <div>
                        <span className="fw-bold">Date: &nbsp;</span>
                        <span>
                          {item.date} ({item.transactionNumber})
                        </span>
                      </div>
                      <div>
                        <span className="fw-bold">Amount: </span>
                        {item.amount.toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                          style: "currency",
                          currency: item?.currencyName ?? "PHP",
                        })}
                      </div>
                      <div className="d-flex align-items-center gap-3 px-3">
                        {/* Pay button */}
                        <div
                          style={{ minWidth: "88px" }}
                          className="text-center"
                        >
                          {item.isPaid ? (
                            <span className="badge px-4 py-2 fs-6 bg-success-subtle text-success border border-success-subtle">
                              Paid
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => {
                                handlePayForecast(item.id);
                              }}
                              className="btn btn-warning w-100"
                            >
                              Pay
                            </button>
                          )}
                        </div>

                        {/* Pass button */}
                        <div
                          style={{ minWidth: "88px" }}
                          className="text-center"
                        >
                          {item.isPassed ? (
                            <span className="badge py-2 fs-6 bg-success-subtle text-success border border-success-subtle">
                              Passed
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() =>
                                handlePassForecast(item.fixed_asset_id, item.id)
                              }
                              className="btn btn-warning w-100"
                            >
                              Pass
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              ))}
            </>
          ) : (
            <div className="alert alert-info">
              <i className="fa-solid fa-info-circle me-2"></i>
              No forecasts exist for the selected dates. Try adjusting the date
              range.
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default OldFixedAsset;
